import React, { useEffect, useRef, useCallback } from 'react'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { useStores } from 'presentation/hooks/use-stores'
import { FormikProvider, useFormik } from 'formik'
import { ReportsAndDocumentsFormV2 } from 'presentation/doctor/components/Forms/AddSurgicalOrderNewV2/ReportsAndDocumentsFormV2'
import { useServices } from 'presentation/hooks/use-services'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import { validationSchema } from './validationSchema'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { UpdateSurgicalOrderNew } from 'domain/usecases/surgical-order/update-surgical-order-new'
import { toast } from 'react-toastify'
import { FinishSurgicalOrder } from 'domain/usecases/surgical-order/finish-surgical-order'
import { useHistory } from 'react-router'
import { UPLOAD_CONFIG } from 'common/utils/upload-config'

const ReportsAndDocuments = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const store = useStores().surgicalOrderV2
    const surgicalOrderService = useServices().surgicalOrder
    const history = useHistory()
    const isDraft = useRef<boolean>(false)

    // Função para verificar se todos os documentos pendentes foram processados
    const verifyPendingDocuments = useCallback(async (): Promise<boolean> => {
      const pendingCount = store.pendingDocumentIds.length

      if (pendingCount === 0) {
        return true
      }

      store.setIsVerifyingDocuments(true)

      try {
        for (
          let attempt = 1;
          attempt <= UPLOAD_CONFIG.POLLING_MAX_ATTEMPTS;
          attempt++
        ) {
          // Busca os documentos atualizados do servidor
          const response = await surgicalOrderService.getSurgicalOrderById({
            surgical_order_id: store.surgicalOrder?.surgicalOrderId
          })

          const serverDocCount = response.documents?.length || 0
          // Documentos na store que não são otimistas (têm document_id numérico real, não Date.now())
          const realDocsInStore =
            store.surgicalOrder.documents?.filter(
              (doc) => doc.document_id < 1000000000000 // IDs reais são menores que timestamp
            ).length || 0

          // Se o servidor tem mais documentos que os reais que tínhamos, alguns foram processados
          // Verificamos se o servidor tem pelo menos (reais + pendentes)
          const expectedMinimum = realDocsInStore + pendingCount

          if (serverDocCount >= expectedMinimum) {
            store.updateStoreSurgicalOrder({
              documents: response.documents
            })
            store.clearPendingDocuments()
            return true
          }

          // Se não é a última tentativa, espera antes de tentar novamente
          if (attempt < UPLOAD_CONFIG.POLLING_MAX_ATTEMPTS) {
            await new Promise((resolve) =>
              setTimeout(resolve, UPLOAD_CONFIG.POLLING_INTERVAL_MS)
            )
          }
        }

        // Timeout - alguns documentos não foram processados
        // Busca o que o servidor tem e mescla com otimistas que ainda não apareceram
        const finalResponse = await surgicalOrderService.getSurgicalOrderById({
          surgical_order_id: store.surgicalOrder?.surgicalOrderId
        })

        // Pega os documentos otimistas que ainda não foram processados (têm ID grande = timestamp)
        const optimisticDocs =
          store.surgicalOrder.documents?.filter(
            (doc) => doc.document_id >= 1000000000000
          ) || []

        // Mescla: documentos reais do servidor + otimistas que ainda estão pendentes
        const mergedDocs = [
          ...(finalResponse.documents || []),
          ...optimisticDocs
        ]

        store.updateStoreSurgicalOrder({
          documents: mergedDocs
        })
        // NÃO limpa pendentes no timeout - mantém para próxima tentativa
        // store.clearPendingDocuments()
        return false
      } finally {
        store.setIsVerifyingDocuments(false)
      }
    }, [store, surgicalOrderService])

    const formik = useFormik({
      initialValues: {
        reportRecommendation:
          toJS(store.surgicalOrder.reportRecommendation) || ''
      },
      validationSchema,
      onSubmit: async (values) => {
        setIsLoading(true)

        // Se não é rascunho e tem documentos pendentes, verifica antes de finalizar
        if (!isDraft.current && store.hasPendingDocuments) {
          const allProcessed = await verifyPendingDocuments()

          if (!allProcessed) {
            toast.warning(
              'Alguns documentos ainda estão sendo processados. Aguarde alguns segundos e tente novamente.'
            )
            setIsLoading(false)
            return
          }
        }

        const payload = {
          surgical_order_id: store.surgicalOrder?.surgicalOrderId,
          patient: {
            patient_id: store.patient?.patientId,
            patient_name: store.patient?.name
          },
          report_recommendation: values.reportRecommendation?.trim()
        }

        const responseUpdate = await updateSurgicalOrder(payload)

        if (responseUpdate) {
          if (isDraft.current) {
            toast.success('As alterações foram salvas')
          } else {
            const responseFinalizeSurgicalOrder = await finalizeSurgicalOrder({
              surgical_order_id: store.surgicalOrder.surgicalOrderId
            })
            if (responseFinalizeSurgicalOrder) {
              store.markStepCompleted(store.currentStepIndex)
              store.clearPendingDocuments() // Limpa pendentes após sucesso
              history.replace('/pedido-sucesso')
            } else {
              toast.error('Tente novamente mais tarde')
            }
          }
        } else {
          toast.error('Tente novamente mais tarde')
        }

        setIsLoading(false)
      }
    })

    const updateSurgicalOrder = async (
      payload: UpdateSurgicalOrderNew.Params
    ) => {
      try {
        await store.updateSurgicalOrder(surgicalOrderService, payload)
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao atualizar os dados do pedido')
        return false
      }
    }

    const finalizeSurgicalOrder = async (
      payload: FinishSurgicalOrder.Params
    ) => {
      try {
        await store.finishSurgicalOrder(surgicalOrderService, payload)
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao enviar pedido cirúrgico')
        return false
      }
    }

    // Sincroniza validação do Formik com o Context para o botão do Layout
    useEffect(() => {
      store.setIsStepValid(formik.isValid && !formik.isSubmitting)
    }, [formik.isValid, formik.isSubmitting])

    // Escuta o clique do botão "Salvar alterações"
    useEffect(() => {
      if (store.saveOnlyTriggered) {
        isDraft.current = true
        formik.submitForm()
        store.resetSaveOnlyTrigger()
      }
    }, [store.saveOnlyTriggered])

    // Escuta o clique do botão que está no Layout
    useEffect(() => {
      if (store.submitFormTriggered) {
        isDraft.current = false
        formik.submitForm()
        store.resetSubmitTrigger()
      }
    }, [store.submitFormTriggered])

    return (
      <>
        <HeadingNew
          as="h1"
          color="textPrimary"
          size="xlarge"
          style={{ lineHeight: 1 }}
        >
          Laudos e documentos
        </HeadingNew>
        <SupportTextNew
          color="textSecondary"
          fontFamily="secondary"
          size="medium"
          style={{ marginBottom: '24px' }}
        >
          Adicione os laudos e documentos necessários
        </SupportTextNew>

        <FormikProvider value={formik}>
          <ReportsAndDocumentsFormV2 />
        </FormikProvider>
      </>
    )
  })
)

export default ReportsAndDocuments
